import { useState, useEffect, useCallback } from 'react';
import { s, authHeaders, Toast, StandardDataGrid, statusPill, gridActionButton, newRecordButtonStyle, getODataRows, resolveODataTotalCount } from './listPageStyles';
import { ProductGroupsPage } from './ProductGroupsPage';
import { ProductSubGroupsPage } from './ProductSubGroupsPage';
import { ProducersPage } from './ProducersPage';

const API_BASE = '/api/products';

const EMPTY_FORM = {
  textId: '', name: '', shortName: '', description: '',
  erpId: '', status: '', unit: '',
  groupId: '', subGroupId: '', producerId: '',
  productImage: '',
};

const LOOKUP_SORT_FIELDS = ['GroupLabel', 'SubGroupLabel', 'ProducerLabel'];
const definitionTabs = [
  { key: 'products', label: 'Ürünler' },
  { key: 'groups', label: 'Ürün Grubu' },
  { key: 'subGroups', label: 'Ek Grup' },
  { key: 'producers', label: 'Üreticiler' },
];

const productDefinitionStyles = {
  tabBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  tab: {
    minHeight: 36,
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-secondary, #4b5563)',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 700,
  },
  tabActive: {
    background: 'var(--accent)',
    borderColor: 'var(--accent)',
    color: '#fff',
  },
};

function unwrapLookupItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}

function toNullableGuid(value) {
  return value ? value : null;
}

// Backend ProductImage artık çıplak base64 (önek yok) ve büyük (1.7 MB'a kadar).
// Dev data URI Chrome'da net::ERR_INVALID_URL'e yol açabildiği için kısa bir Blob URL üretip
// onu kullanıyoruz. Değer zaten data:/http ise (örn. yeni yüklenen dosya) doğrudan kullanılır.
function useImageObjectUrl(value) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!value) { setUrl(''); return undefined; }
    if (/^(data:|https?:)/i.test(value)) { setUrl(value); return undefined; }
    let objectUrl = '';
    try {
      // Import artefaktlarını temizle: base64 alfabesi dışındaki her şeyi (boşluk,
      // satır sonu, CSV'den yapışmış stray tırnak " vb.) ayıkla.
      let clean = value.replace(/[^A-Za-z0-9+/=]/g, '');
      // Padding'siz base64 olabilir; atob için '=' dolgusunu geri ekle.
      const pad = clean.length % 4;
      if (pad === 2) clean += '==';
      else if (pad === 3) clean += '=';
      const binary = atob(clean);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      // MIME tipini base64 imzasından belirle (PNG / JPEG / GIF / WEBP)
      const mime = clean.startsWith('/9j/') ? 'image/jpeg'
        : clean.startsWith('R0lGOD') ? 'image/gif'
        : clean.startsWith('UklGR') ? 'image/webp'
        : 'image/png';
      objectUrl = URL.createObjectURL(new Blob([bytes], { type: mime }));
      setUrl(objectUrl);
    } catch {
      setUrl('');
    }
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [value]);
  return url;
}

function ProductThumb({ value }) {
  const url = useImageObjectUrl(value);
  if (!url) return '—';
  return <img src={url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: '50%', border: '1px solid var(--border-color, #e5e7eb)' }} />;
}

function ProductImagePreview({ value, name }) {
  const url = useImageObjectUrl(value);
  if (!url) return null;
  return (
    <img
      src={url}
      alt={name || 'Ürün görseli'}
      style={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, #f9fafb)' }}
    />
  );
}

// Kaydederken kolonu çıplak base64 olarak tut (yeni veri formatıyla tutarlı):
// data URI ise "base64," sonrasını al, değilse olduğu gibi bırak.
function toRawBase64(value) {
  if (!value) return null;
  const idx = value.indexOf('base64,');
  return idx >= 0 ? value.slice(idx + 'base64,'.length) : value;
}

function toRequiredInt(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function compareText(a, b, direction) {
  const left = (a || '').toString().toLocaleLowerCase('tr-TR');
  const right = (b || '').toString().toLocaleLowerCase('tr-TR');
  const result = left.localeCompare(right, 'tr');
  return direction === 'asc' ? result : -result;
}

export function ProductsPageLegacy() {
  const [activeDefinition, setActiveDefinition] = useState('products');
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  const [sortField, setSortField] = useState('TextId');
  const [sortDir, setSortDir] = useState('asc');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [groups, setGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);
  const [producers, setProducers] = useState([]);
  const [additionalFields, setAdditionalFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});

  const getGroupLabel = useCallback((groupId) => {
    const group = groups.find(item => item.id === groupId);
    return group?.description || group?.name || group?.textId || '-';
  }, [groups]);

  const getSubGroupLabel = useCallback((subGroupId) => {
    const subGroup = subGroups.find(item => item.id === subGroupId);
    return subGroup?.description || subGroup?.name || subGroup?.textId || '-';
  }, [subGroups]);

  const getProducerLabel = useCallback((producerId) => producers.find(item => item.id === producerId)?.name || '-', [producers]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/productgroups/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/productsubgroups/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/producers/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/productadditionalfields/Select', { headers }).then(res => res.ok ? res.json() : []),
    ]).then(([groupData, subGroupData, producerData, fieldData]) => {
      setGroups(unwrapLookupItems(groupData));
      setSubGroups(unwrapLookupItems(subGroupData));
      setProducers(unwrapLookupItems(producerData));
      setAdditionalFields(unwrapLookupItems(fieldData));
    }).catch(() => {});
  }, []);

  // Bir ürünün mevcut özel alan değerlerini { [fieldId]: value } olarak getir.
  const loadFieldValues = useCallback(async (productId) => {
    if (!productId) { setFieldValues({}); return; }
    try {
      const res = await fetch(`/api/productadditionalfields/ValuesForProduct?productId=${productId}`, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      const items = unwrapLookupItems(body?.data ?? body?.Data ?? body);
      const map = {};
      items.forEach(it => { map[it.additionalFieldId ?? it.AdditionalFieldId] = it.value ?? it.Value ?? ''; });
      setFieldValues(map);
    } catch {
      setFieldValues({});
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let filterParams = '';
      if (filterText.trim()) {
        const q = filterText.trim().replace(/'/g, "''");
        filterParams = `(contains(tolower(TextId),'${q.toLowerCase()}') or contains(tolower(Name),'${q.toLowerCase()}') or contains(tolower(ShortName),'${q.toLowerCase()}'))`;
      }

      const skip = (currentPage - 1) * pageSize;
      const isLookupSort = LOOKUP_SORT_FIELDS.includes(sortField);
      let url = `${API_BASE}?$count=true`;

      if (!isLookupSort) {
        url += `&$top=${pageSize}&$skip=${skip}`;
      }
      if (filterParams) {
        url += `&$filter=${encodeURIComponent(filterParams)}`;
      }
      if (!isLookupSort) {
        url += `&$orderby=${encodeURIComponent(`${sortField} ${sortDir}`)}`;
      }

      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alınamadı');

      const data = await res.json();
      const allRows = getODataRows(data);

      if (isLookupSort) {
        const sortedRows = [...allRows].sort((a, b) => {
          if (sortField === 'GroupLabel') {
            return compareText(getGroupLabel(a.groupId), getGroupLabel(b.groupId), sortDir);
          }
          if (sortField === 'SubGroupLabel') {
            return compareText(getSubGroupLabel(a.subGroupId), getSubGroupLabel(b.subGroupId), sortDir);
          }
          return compareText(getProducerLabel(a.producerId), getProducerLabel(b.producerId), sortDir);
        });

        setRows(sortedRows.slice(skip, skip + pageSize));
        setTotalCount(sortedRows.length);
      } else {
        setRows(allRows);
        setTotalCount(await resolveODataTotalCount({ data, rows: allRows, currentPage, pageSize, base: API_BASE, filterParams, headers: authHeaders() }));
      }
    } catch (error) {
      showToast('error', error.message || 'Veri alınırken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [filterText, sortField, sortDir, pageSize, currentPage, getGroupLabel, getSubGroupLabel, getProducerLabel]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilterSearch = () => {
    setFilterText(filterInput);
    setCurrentPage(1);
  };

  const handleFilterKeyDown = (event) => {
    if (event.key === 'Enter') handleFilterSearch();
  };

  const clearFilter = () => {
    setFilterText('');
    setFilterInput('');
    setCurrentPage(1);
  };

  const rowToForm = (row) => ({
    textId: row.textId || '',
    name: row.name || '',
    shortName: row.shortName || '',
    description: row.description || '',
    erpId: row.erpId || '',
    status: row.status || '',
    unit: row.unit || '',
    groupId: row.groupId || '',
    subGroupId: row.subGroupId || '',
    producerId: row.producerId || '',
    productImage: row.productImage || '',
  });

  const openNew = () => {
    setForm(EMPTY_FORM);
    setSelected(null);
    setFieldValues({});
    setModal('new');
  };

  const openEdit = (row = selected) => {
    if (!row) return;
    setSelected(row);
    setForm(rowToForm(row));
    loadFieldValues(row.id);
    setModal('edit');
  };

  const openView = (row = selected) => {
    if (!row) return;
    setSelected(row);
    setForm(rowToForm(row));
    loadFieldValues(row.id);
    setModal('view');
  };

  const closeModal = () => setModal(null);

  const handleImageFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      showToast('error', "Görsel 2 MB'dan küçük olmalı");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm(current => ({ ...current, productImage: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.textId.trim()) {
      showToast('error', 'Ürün kodu boş olamaz');
      return;
    }
    if (!form.name.trim()) {
      showToast('error', 'Ürün adı boş olamaz');
      return;
    }

    // Zorunlu özel alan kontrolü
    const missingRequired = additionalFields.find(f => f.isRequired && !String(fieldValues[f.id] ?? '').trim());
    if (missingRequired) {
      showToast('error', `"${missingRequired.name}" alanı zorunludur`);
      return;
    }

    const productId = modal === 'new'
      ? (window.crypto?.randomUUID ? window.crypto.randomUUID() : undefined)
      : selected.id;

    const payload = {
      textId: form.textId,
      name: form.name,
      shortName: form.shortName || null,
      description: form.description || null,
      erpId: form.erpId || null,
      status: toRequiredInt(form.status),
      unit: toRequiredInt(form.unit),
      groupId: toNullableGuid(form.groupId),
      subGroupId: toNullableGuid(form.subGroupId),
      producerId: toNullableGuid(form.producerId),
      productImage: toRawBase64(form.productImage),
    };

    const saveFieldValues = async (pid) => {
      if (!pid || additionalFields.length === 0) return;
      await fetch('/api/productadditionalfields/SaveValues', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          productId: pid,
          values: additionalFields.map(f => ({ additionalFieldId: f.id, value: String(fieldValues[f.id] ?? '') })),
        }),
      });
    };

    try {
      if (modal === 'new') {
        const res = await fetch(`${API_BASE}/Add`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ ...payload, id: productId }),
        });
        const body = await res.json().catch(() => null);
        if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt eklenemedi');
        await saveFieldValues(productId);
        showToast('success', 'Ürün başarıyla eklendi');
      } else {
        const res = await fetch(`${API_BASE}/Update`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ id: selected.id, ...payload }),
        });
        const body = await res.json().catch(() => null);
        if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt güncellenemedi');
        await saveFieldValues(selected.id);
        showToast('success', 'Ürün başarıyla güncellendi');
      }
      closeModal();
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'İşlem sırasında hata oluştu');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ id: selected.id }),
      });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Ürün başarıyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const isView = modal === 'view';
  const inputStyle = isView ? s.inputView : s.input;
  const selectStyle = isView ? s.selectView : s.select;
  const columns = [
    { field: 'textId', headerName: 'Ürün Kodu', flex: 1, minWidth: 140 },
    { field: 'name', headerName: 'Adı', flex: 1.4, minWidth: 200 },
    { field: 'shortName', headerName: 'Kısa Adı', flex: 1, minWidth: 130, renderCell: (params) => params.row.shortName || '-' },
    { field: 'unit', headerName: 'Birim', width: 100, renderCell: (params) => params.row.unit ?? '-' },
    { field: 'status', headerName: 'Durum', width: 130, renderCell: (params) => statusPill(params.row.status) },
    {
      field: 'productImage',
      headerName: 'Görsel',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => <ProductThumb value={params.row.productImage} />,
    },
    { field: 'groupLabel', headerName: 'Ürün Grubu', flex: 1.2, minWidth: 170, renderCell: (params) => getGroupLabel(params.row.groupId) },
    { field: 'subGroupLabel', headerName: 'Ek Grubu', flex: 1.1, minWidth: 160, renderCell: (params) => getSubGroupLabel(params.row.subGroupId) },
    { field: 'producerLabel', headerName: 'Üretici', flex: 1.1, minWidth: 160, renderCell: (params) => getProducerLabel(params.row.producerId) },
    {
      field: 'actions',
      headerName: 'İşlemler',
      width: 250,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: '100%' }}>
          <button style={{ ...gridActionButton, background: 'var(--accent)' }} onClick={(event) => { event.stopPropagation(); openView(params.row); }}>Görüntüle</button>
          <button style={{ ...gridActionButton, background: 'var(--warn)' }} onClick={(event) => { event.stopPropagation(); openEdit(params.row); }}>Değiştir</button>
          <button style={{ ...gridActionButton, background: '#E53E3E', minWidth: 48 }} onClick={(event) => { event.stopPropagation(); setSelected(params.row); setDeleteConfirm(true); }}>Sil</button>
        </div>
      ),
    },
  ];

  const renderDefinitionTabs = () => (
    <div style={productDefinitionStyles.tabBar}>
      {definitionTabs.map(tab => (
        <button
          key={tab.key}
          type="button"
          style={{
            ...productDefinitionStyles.tab,
            ...(activeDefinition === tab.key ? productDefinitionStyles.tabActive : {}),
          }}
          onClick={() => setActiveDefinition(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  if (activeDefinition === 'groups') {
    return (
      <div style={s.container}>
        {renderDefinitionTabs()}
        <ProductGroupsPage />
      </div>
    );
  }

  if (activeDefinition === 'subGroups') {
    return (
      <div style={s.container}>
        {renderDefinitionTabs()}
        <ProductSubGroupsPage />
      </div>
    );
  }

  if (activeDefinition === 'producers') {
    return (
      <div style={s.container}>
        {renderDefinitionTabs()}
        <ProducersPage />
      </div>
    );
  }

  return (
    <div style={s.container}>
      {renderDefinitionTabs()}
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h2 style={s.pageTitle}>Ürün Tanım Yönetimi</h2>
          <p style={s.pageSubtitle}>Ürün kayıtlarını filtreleyin, sıralayın ve detaylarıyla yönetin.</p>
        </div>
        <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Ürün</button>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <input
            style={s.filterInput}
            type="text"
            placeholder="Filtrele... (Enter)"
            value={filterInput}
            onChange={e => setFilterInput(e.target.value)}
            onKeyDown={handleFilterKeyDown}
          />
          {filterText && <button style={s.btnSecondary} onClick={clearFilter}>Temizle</button>}
          <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
        </div>
      </div>

      <StandardDataGrid
        rows={rows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={page => setCurrentPage(page)}
        onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, direction) => { setCurrentPage(1); setSortField(field || 'TextId'); setSortDir(direction || 'asc'); }}
        selectedId={selected?.id}
        onRowClick={row => setSelected(row)}
        onRowDoubleClick={row => openView(row)}
        exportFileName="urunler"
      />

      {modal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={{ ...s.modal, maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {modal === 'new' ? 'Yeni Ürün' : modal === 'edit' ? 'Ürün Düzenle' : 'Ürün Detay'}
              </h3>
              <button style={s.closeBtn} onClick={closeModal}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Ürün Kodu *</label>
                  <input
                    style={inputStyle}
                    value={form.textId}
                    onChange={e => setForm(current => ({ ...current, textId: e.target.value }))}
                    readOnly={isView}
                    placeholder="Ürün kodunu giriniz"
                    autoFocus
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Ürün Adı *</label>
                  <input
                    style={inputStyle}
                    value={form.name}
                    onChange={e => setForm(current => ({ ...current, name: e.target.value }))}
                    readOnly={isView}
                    placeholder="Ürün adını giriniz"
                  />
                </div>
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Kısa Adı</label>
                  <input
                    style={inputStyle}
                    value={form.shortName}
                    onChange={e => setForm(current => ({ ...current, shortName: e.target.value }))}
                    readOnly={isView}
                    placeholder="Kısa ad giriniz"
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>ERP Kodu</label>
                  <input
                    style={inputStyle}
                    value={form.erpId}
                    onChange={e => setForm(current => ({ ...current, erpId: e.target.value }))}
                    readOnly={isView}
                    placeholder="ERP kodu giriniz"
                  />
                </div>
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Birim</label>
                  <input
                    style={inputStyle}
                    value={form.unit}
                    onChange={e => setForm(current => ({ ...current, unit: e.target.value }))}
                    readOnly={isView}
                    placeholder="Birim giriniz"
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <input
                    style={inputStyle}
                    value={form.status}
                    onChange={e => setForm(current => ({ ...current, status: e.target.value }))}
                    readOnly={isView}
                    placeholder="Durum giriniz"
                  />
                </div>
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Ürün Grubu</label>
                  {isView ? (
                    <input style={s.inputView} readOnly value={getGroupLabel(form.groupId)} />
                  ) : (
                    <select style={selectStyle} value={form.groupId} onChange={e => setForm(current => ({ ...current, groupId: e.target.value }))}>
                      <option value="">Seçiniz</option>
                      {groups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.textId}{group.description ? ` - ${group.description}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Ek Grubu</label>
                  {isView ? (
                    <input style={s.inputView} readOnly value={getSubGroupLabel(form.subGroupId)} />
                  ) : (
                    <select style={selectStyle} value={form.subGroupId} onChange={e => setForm(current => ({ ...current, subGroupId: e.target.value }))}>
                      <option value="">Seçiniz</option>
                      {subGroups.map(subGroup => (
                        <option key={subGroup.id} value={subGroup.id}>
                          {subGroup.textId}{subGroup.description ? ` - ${subGroup.description}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Üretici</label>
                  {isView ? (
                    <input style={s.inputView} readOnly value={getProducerLabel(form.producerId)} />
                  ) : (
                    <select style={selectStyle} value={form.producerId} onChange={e => setForm(current => ({ ...current, producerId: e.target.value }))}>
                      <option value="">Seçiniz</option>
                      {producers.map(producer => (
                        <option key={producer.id} value={producer.id}>
                          {producer.textId} - {producer.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Açıklama</label>
                  <input
                    style={inputStyle}
                    value={form.description}
                    onChange={e => setForm(current => ({ ...current, description: e.target.value }))}
                    readOnly={isView}
                    placeholder="Açıklama giriniz"
                  />
                </div>
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>Ürün Görseli</label>
                <ProductImagePreview value={form.productImage} name={form.name} />
                {!isView && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: form.productImage ? 8 : 0 }}>
                    <input type="file" accept="image/*" onChange={handleImageFile} />
                    {form.productImage && (
                      <button
                        type="button"
                        style={s.btnSecondary}
                        onClick={() => setForm(current => ({ ...current, productImage: '' }))}
                      >
                        Görseli kaldır
                      </button>
                    )}
                  </div>
                )}
                {isView && !form.productImage && (
                  <span style={{ color: 'var(--text-secondary, #6b7280)', fontSize: 13 }}>Görsel yok</span>
                )}
              </div>

              {additionalFields.length > 0 && (
                <div style={{ borderTop: '1px solid var(--border-color, #e5e7eb)', paddingTop: 14 }}>
                  <label style={{ ...s.label, fontSize: 14, marginBottom: 8, display: 'block' }}>Özel Alanlar</label>
                  <div style={s.formRow}>
                    {additionalFields.map(field => {
                      const val = fieldValues[field.id] ?? '';
                      const setVal = (v) => setFieldValues(current => ({ ...current, [field.id]: v }));
                      const dt = Number(field.dataType);
                      return (
                        <div style={s.formGroup} key={field.id}>
                          <label style={s.label}>{field.name}{field.isRequired ? ' *' : ''}</label>
                          {dt === 4 ? (
                            isView ? (
                              <input style={s.inputView} readOnly value={val === 'true' ? 'Evet' : val === 'false' ? 'Hayır' : ''} />
                            ) : (
                              <select style={selectStyle} value={val} onChange={e => setVal(e.target.value)}>
                                <option value="">Seçiniz</option>
                                <option value="true">Evet</option>
                                <option value="false">Hayır</option>
                              </select>
                            )
                          ) : (
                            <input
                              style={inputStyle}
                              type={dt === 2 ? 'number' : dt === 3 ? 'date' : 'text'}
                              value={val}
                              onChange={e => setVal(e.target.value)}
                              readOnly={isView}
                              placeholder={isView ? '' : `${field.name} giriniz`}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div style={s.modalFooter}>
              {!isView && <button style={s.btnPrimary} onClick={handleSave}>Kaydet</button>}
              <button style={s.btnSecondary} onClick={closeModal}>{isView ? 'Kapat' : 'İptal'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>"{selected?.name}"</strong> ürününü silmek istediğinize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
